import { useState, useEffect } from 'react';
import { Plus, Settings as SettingsIcon, Edit, Trash2, Tag, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';
import { toast } from 'sonner';

function Configuracoes() {
  const [categorias, setCategorias] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [configOficina, setConfigOficina] = useState({ 
    nome_oficina: 'AutoFlow Pro', 
    logo_url: '',
    cnpj: '',
    telefone: '',
    email: '',
    endereco: '',
    cidade: '',
    estado: ''
  });
  const [activeDialog, setActiveDialog] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const [categoriaForm, setCategoriaForm] = useState({ nome: '', descricao: '' });
  const [fornecedorForm, setFornecedorForm] = useState({ nome: '', contato: '', email: '', telefone: '' });
  const [templateForm, setTemplateForm] = useState({ nome: '', descricao: '', valor_padrao: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [categoriasRes, fornecedoresRes, templatesRes, configRes] = await Promise.all([
        api.get('/categorias'),
        api.get('/fornecedores'),
        api.get('/templates'),
        api.get('/configuracoes/oficina')
      ]);
      setCategorias(categoriasRes.data);
      setFornecedores(fornecedoresRes.data);
      setTemplates(templatesRes.data);
      setConfigOficina(configRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    }
  };

  const handleSubmitCategoria = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/categorias/${editingItem.id}`, categoriaForm);
        toast.success('Categoria atualizada');
      } else {
        await api.post('/categorias', categoriaForm);
        toast.success('Categoria cadastrada');
      }
      setActiveDialog(null);
      setCategoriaForm({ nome: '', descricao: '' });
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar categoria');
    }
  };

  const handleSubmitFornecedor = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/fornecedores/${editingItem.id}`, fornecedorForm);
        toast.success('Fornecedor atualizado');
      } else {
        await api.post('/fornecedores', fornecedorForm);
        toast.success('Fornecedor cadastrado');
      }
      setActiveDialog(null);
      setFornecedorForm({ nome: '', contato: '', email: '', telefone: '' });
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar fornecedor');
    }
  };

  const handleSubmitTemplate = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/templates/${editingItem.id}`, templateForm);
        toast.success('Template atualizado');
      } else {
        await api.post('/templates', templateForm);
        toast.success('Template cadastrado');
      }
      setActiveDialog(null);
      setTemplateForm({ nome: '', descricao: '', valor_padrao: 0 });
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar template');
    }
  };

  const handleDeleteCategoria = async (id) => {
    if (window.confirm('Tem certeza?')) {
      try {
        await api.delete(`/categorias/${id}`);
        toast.success('Categoria deletada');
        loadData();
      } catch (error) {
        toast.error('Erro ao deletar');
      }
    }
  };

  const handleDeleteFornecedor = async (id) => {
    if (window.confirm('Tem certeza?')) {
      try {
        await api.delete(`/fornecedores/${id}`);
        toast.success('Fornecedor deletado');
        loadData();
      } catch (error) {
        toast.error('Erro ao deletar');
      }
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Tem certeza?')) {
      try {
        await api.delete(`/templates/${id}`);
        toast.success('Template deletado');
        loadData();
      } catch (error) {
        toast.error('Erro ao deletar');
      }
    }
  };

  const handleSaveConfigOficina = async (e) => {
    e.preventDefault();
    try {
      await api.put('/configuracoes/oficina', configOficina);
      toast.success('Configurações da oficina atualizadas!');
      loadData();
      // Recarregar página para atualizar sidebar
      window.location.reload();
    } catch (error) {
      toast.error('Erro ao salvar configurações');
      console.error(error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <div className="p-8" data-testid="configuracoes-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white uppercase tracking-tight mb-2">Configurações</h1>
        <p className="text-slate-500">Gerencie categorias, fornecedores e templates</p>
      </div>

      <Tabs defaultValue="oficina" className="space-y-6">
        <TabsList className="bg-carbon border border-white/10">
          <TabsTrigger value="oficina" className="data-[state=active]:bg-electric-blue data-[state=active]:text-white">
            <Wrench className="w-4 h-4 mr-2" />
            Oficina
          </TabsTrigger>
          <TabsTrigger value="categorias" className="data-[state=active]:bg-electric-blue data-[state=active]:text-white">
            <Tag className="w-4 h-4 mr-2" />
            Categorias
          </TabsTrigger>
          <TabsTrigger value="fornecedores" className="data-[state=active]:bg-electric-blue data-[state=active]:text-white">
            <SettingsIcon className="w-4 h-4 mr-2" />
            Fornecedores
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-electric-blue data-[state=active]:text-white">
            <Wrench className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
        </TabsList>

        {/* Oficina */}
        <TabsContent value="oficina">
          <div className="bg-carbon border border-white/5 rounded-md p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-white mb-4 uppercase tracking-wide">Dados da Oficina</h3>
            <p className="text-xs text-slate-500 mb-6">Essas informações aparecem no recibo de serviço</p>
            <form onSubmit={handleSaveConfigOficina} className="space-y-4 max-w-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Nome da Oficina</Label>
                  <Input
                    value={configOficina.nome_oficina}
                    onChange={(e) => setConfigOficina({ ...configOficina, nome_oficina: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder="Ex: Oficina do João"
                    required
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">CNPJ</Label>
                  <Input
                    value={configOficina.cnpj || ''}
                    onChange={(e) => setConfigOficina({ ...configOficina, cnpj: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder="00.000.000/0001-00"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Telefone</Label>
                  <Input
                    value={configOficina.telefone || ''}
                    onChange={(e) => setConfigOficina({ ...configOficina, telefone: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
                  <Input
                    type="email"
                    value={configOficina.email || ''}
                    onChange={(e) => setConfigOficina({ ...configOficina, email: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder="contato@oficina.com"
                  />
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Endereço</Label>
                  <Input
                    value={configOficina.endereco || ''}
                    onChange={(e) => setConfigOficina({ ...configOficina, endereco: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder="Rua, número, bairro"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Cidade</Label>
                  <Input
                    value={configOficina.cidade || ''}
                    onChange={(e) => setConfigOficina({ ...configOficina, cidade: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder="São Paulo"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Estado</Label>
                  <Input
                    value={configOficina.estado || ''}
                    onChange={(e) => setConfigOficina({ ...configOficina, estado: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder="SP"
                    maxLength={2}
                  />
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">URL do Logo</Label>
                  <Input
                    value={configOficina.logo_url || ''}
                    onChange={(e) => setConfigOficina({ ...configOficina, logo_url: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder="https://exemplo.com/logo.png"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Cole o link da imagem do seu logo (opcional)
                  </p>
                </div>
              </div>

              {configOficina.logo_url && (
                <div className="bg-obsidian p-4 rounded-md">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Preview do Logo</Label>
                  <img 
                    src={configOficina.logo_url} 
                    alt="Logo preview" 
                    className="h-16 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      toast.error('URL do logo inválida');
                    }}
                  />
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white">
                  Salvar Configurações
                </Button>
              </div>
            </form>
          </div>
        </TabsContent>

        {/* Categorias */}
        <TabsContent value="categorias">
          <div className="flex justify-end mb-4">
            <Dialog open={activeDialog === 'categoria'} onOpenChange={(open) => {
              setActiveDialog(open ? 'categoria' : null);
              if (!open) { setCategoriaForm({ nome: '', descricao: '' }); setEditingItem(null); }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm" data-testid="add-categoria-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Categoria
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-carbon border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold uppercase">
                    {editingItem ? 'Editar Categoria' : 'Nova Categoria'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitCategoria} className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Nome</Label>
                    <Input
                      value={categoriaForm.nome}
                      onChange={(e) => setCategoriaForm({ ...categoriaForm, nome: e.target.value })}
                      className="bg-obsidian border-white/10 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Descrição</Label>
                    <Input
                      value={categoriaForm.descricao}
                      onChange={(e) => setCategoriaForm({ ...categoriaForm, descricao: e.target.value })}
                      className="bg-obsidian border-white/10 text-white"
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button type="button" variant="ghost" onClick={() => setActiveDialog(null)} className="text-slate-400">
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white">
                      Salvar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-carbon border border-white/5 rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/2 border-b border-white/5">
                <tr>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Nome</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Descrição</th>
                  <th className="text-right px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {categorias.map((cat) => (
                  <tr key={cat.id} className="table-row border-b border-white/5">
                    <td className="px-6 py-4 text-sm text-slate-300">{cat.nome}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{cat.descricao || '-'}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingItem(cat);
                            setCategoriaForm({ nome: cat.nome, descricao: cat.descricao || '' });
                            setActiveDialog('categoria');
                          }}
                          className="text-electric-blue hover:text-white hover:bg-white/5"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteCategoria(cat.id)}
                          className="text-alert-red hover:text-white hover:bg-white/5"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Fornecedores */}
        <TabsContent value="fornecedores">
          <div className="flex justify-end mb-4">
            <Dialog open={activeDialog === 'fornecedor'} onOpenChange={(open) => {
              setActiveDialog(open ? 'fornecedor' : null);
              if (!open) { setFornecedorForm({ nome: '', contato: '', email: '', telefone: '' }); setEditingItem(null); }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm" data-testid="add-fornecedor-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Fornecedor
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-carbon border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold uppercase">
                    {editingItem ? 'Editar Fornecedor' : 'Novo Fornecedor'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitFornecedor} className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Nome</Label>
                    <Input
                      value={fornecedorForm.nome}
                      onChange={(e) => setFornecedorForm({ ...fornecedorForm, nome: e.target.value })}
                      className="bg-obsidian border-white/10 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Contato</Label>
                    <Input
                      value={fornecedorForm.contato}
                      onChange={(e) => setFornecedorForm({ ...fornecedorForm, contato: e.target.value })}
                      className="bg-obsidian border-white/10 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
                      <Input
                        type="email"
                        value={fornecedorForm.email}
                        onChange={(e) => setFornecedorForm({ ...fornecedorForm, email: e.target.value })}
                        className="bg-obsidian border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-400">Telefone</Label>
                      <Input
                        value={fornecedorForm.telefone}
                        onChange={(e) => setFornecedorForm({ ...fornecedorForm, telefone: e.target.value })}
                        className="bg-obsidian border-white/10 text-white"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button type="button" variant="ghost" onClick={() => setActiveDialog(null)} className="text-slate-400">
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white">
                      Salvar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-carbon border border-white/5 rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/2 border-b border-white/5">
                <tr>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Nome</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Email</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Telefone</th>
                  <th className="text-right px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {fornecedores.map((forn) => (
                  <tr key={forn.id} className="table-row border-b border-white/5">
                    <td className="px-6 py-4 text-sm text-slate-300">{forn.nome}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{forn.email || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{forn.telefone || '-'}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingItem(forn);
                            setFornecedorForm({ nome: forn.nome, contato: forn.contato || '', email: forn.email || '', telefone: forn.telefone || '' });
                            setActiveDialog('fornecedor');
                          }}
                          className="text-electric-blue hover:text-white hover:bg-white/5"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteFornecedor(forn.id)}
                          className="text-alert-red hover:text-white hover:bg-white/5"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Templates */}
        <TabsContent value="templates">
          <div className="flex justify-end mb-4">
            <Dialog open={activeDialog === 'template'} onOpenChange={(open) => {
              setActiveDialog(open ? 'template' : null);
              if (!open) { setTemplateForm({ nome: '', descricao: '', valor_padrao: 0 }); setEditingItem(null); }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm" data-testid="add-template-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Template
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-carbon border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold uppercase">
                    {editingItem ? 'Editar Template' : 'Novo Template'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitTemplate} className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Nome do Serviço</Label>
                    <Input
                      value={templateForm.nome}
                      onChange={(e) => setTemplateForm({ ...templateForm, nome: e.target.value })}
                      className="bg-obsidian border-white/10 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Descrição</Label>
                    <Input
                      value={templateForm.descricao}
                      onChange={(e) => setTemplateForm({ ...templateForm, descricao: e.target.value })}
                      className="bg-obsidian border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Valor Padrão</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={templateForm.valor_padrao}
                      onChange={(e) => setTemplateForm({ ...templateForm, valor_padrao: parseFloat(e.target.value) || 0 })}
                      className="bg-obsidian border-white/10 text-white"
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button type="button" variant="ghost" onClick={() => setActiveDialog(null)} className="text-slate-400">
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white">
                      Salvar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-carbon border border-white/5 rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/2 border-b border-white/5">
                <tr>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Serviço</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Descrição</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Valor</th>
                  <th className="text-right px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((temp) => (
                  <tr key={temp.id} className="table-row border-b border-white/5">
                    <td className="px-6 py-4 text-sm text-slate-300">{temp.nome}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{temp.descricao || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-300 font-semibold">{formatCurrency(temp.valor_padrao)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingItem(temp);
                            setTemplateForm({ nome: temp.nome, descricao: temp.descricao || '', valor_padrao: temp.valor_padrao });
                            setActiveDialog('template');
                          }}
                          className="text-electric-blue hover:text-white hover:bg-white/5"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteTemplate(temp.id)}
                          className="text-alert-red hover:text-white hover:bg-white/5"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Configuracoes;